// Angular
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

// RxJS
import { catchError, filter, finalize, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';

// App services
import { AuthApiService } from '@app/features/auth/services/auth-api.service';
import { SessionCleanupService } from './session-cleanup.service';
import { TokenService } from './token.service';


@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private readonly authApi = inject(AuthApiService);
  private readonly tokenService = inject(TokenService);
  private readonly sessionCleanup = inject(SessionCleanupService);
  private readonly router = inject(Router);

  private refreshInFlight$: Observable<string> | null = null;

  ensureAccessToken(): Observable<boolean> {
    if (this.tokenService.hasAccessToken()) {
      return of(true);
    }

    if (!this.tokenService.hasRefreshToken()) {
      return of(false);
    }

    return this.refresh().pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  refresh(): Observable<string> {
    const refreshToken = this.tokenService.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('NO_REFRESH_TOKEN'));
    }

    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.authApi.refreshAccessToken(JSON.stringify(refreshToken)).pipe(
        filter(response => response.success),
        map(response => response.result),
        tap(accessToken => this.tokenService.setAccessToken(accessToken)),
        catchError(error => {
          this.clearSession();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );
    }

    return this.refreshInFlight$;
  }

  clearSession(): void {
    this.tokenService.removeTokens();
    this.sessionCleanup.clearCachedUserData();
    void this.router.navigate(['/auth/identify']);
  }
}
